/* ===== Where a journal entry / a map lives (P03) =====
 *
 * `owner` says which beat, quest or quest objective an entry belongs to; this
 * resolves it against what exists NOW. An owner that no longer exists (a
 * deleted quest, a removed objective) resolves to null: such an entry is shown
 * with the shared ones, never hidden without a way to open it.
 */

import type { JournalEntryOwner, MapOwner } from '@roll1/shared';
import type { TranslationKeys } from '../../i18n/types.js';

/** The beat's name as the rail shows it (a fresh beat without a name is «Бит N»). */
export function beatDisplayName(
  beat: { readonly name?: string | null; readonly sessionLabel?: string | null; readonly order: number },
  t: (key: keyof TranslationKeys) => string,
): string {
  const explicit = beat.name ?? beat.sessionLabel;
  if (explicit !== undefined && explicit !== null && explicit !== '') return explicit;
  return `${t('studio.beat.one')} ${beat.order}`;
}

export interface OwnerLookup {
  readonly beatExists: (beatId: string) => boolean;
  /** The quest entry and its objectives (id, text); undefined when there is no such quest. */
  readonly quest: (questId: string) => { readonly objectives: ReadonlyArray<{ readonly id: string; readonly text: string }> } | undefined;
}

export type EntryPlace =
  | { readonly kind: 'beat'; readonly beatId: string }
  | { readonly kind: 'quest'; readonly questId: string }
  | { readonly kind: 'objective'; readonly questId: string; readonly objectiveId: string; readonly objectiveText: string };

/** null = shared (no owner, explicitly shared, or an owner that does not exist any more). */
export function entryPlace(owner: JournalEntryOwner | null | undefined, lookup: OwnerLookup): EntryPlace | null {
  if (owner === undefined || owner === null) return null;
  switch (owner.kind) {
    case 'shared':
      return null;
    case 'beat':
      return lookup.beatExists(owner.beatId) ? { kind: 'beat', beatId: owner.beatId } : null;
    case 'quest':
      return lookup.quest(owner.questId) !== undefined ? { kind: 'quest', questId: owner.questId } : null;
    case 'objective': {
      const objective = lookup.quest(owner.questId)?.objectives.find((o) => o.id === owner.objectiveId);
      return objective === undefined
        ? null
        : { kind: 'objective', questId: owner.questId, objectiveId: owner.objectiveId, objectiveText: objective.text };
    }
  }
}

/** The beat a map lives in, or null (shared, or the beat does not exist any more). */
export function mapBeatId(owner: MapOwner | null | undefined, lookup: Pick<OwnerLookup, 'beatExists'>): string | null {
  return owner !== undefined && owner !== null && owner.kind === 'beat' && lookup.beatExists(owner.beatId) ? owner.beatId : null;
}
